import { useState } from "react";

import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

import { DeleteNamedResourceDialog } from "./DeleteNamedResourceDialog";
import { NamedResourceFormDialog } from "./NamedResourceFormDialog";
import type { NamedResourceAdapter } from "./types";

type FormDialogState<TItem> =
  | { open: false }
  | { open: true; mode: "create" }
  | { open: true; mode: "edit"; item: TItem };

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toISOString().slice(0, 10);
}

export type NamedResourceCRUDPageProps<TItem> = {
  adapter: NamedResourceAdapter<TItem>;
};

export function NamedResourceCRUDPage<TItem>({
  adapter,
}: NamedResourceCRUDPageProps<TItem>) {
  const [showDeleted, setShowDeleted] = useState(false);
  const [formDialog, setFormDialog] = useState<FormDialogState<TItem>>({
    open: false,
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item: TItem | null;
  }>({ open: false, item: null });

  const list = adapter.useList({ include_deleted: showDeleted });
  const restoreMutation = adapter.useRestore();

  const items = list.data?.items ?? [];

  const loadErrorMessage =
    list.error instanceof Error ? list.error.message : adapter.loadErrorMessage;

  return (
    <section className="p-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            {adapter.pluralLabelCapitalized}
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">{adapter.pageDescription}</p>
        </div>
        <Button
          type="button"
          onClick={() => {
            setFormDialog({ open: true, mode: "create" });
          }}
        >
          New {adapter.singularLabel}
        </Button>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>{adapter.pluralLabelCapitalized}</CardTitle>
          <label className="flex items-center gap-2 text-sm text-on-surface-variant">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={showDeleted}
              onChange={(event) => {
                setShowDeleted(event.target.checked);
              }}
            />
            Show deleted
          </label>
        </CardHeader>
        <CardContent>
          {list.isLoading ? (
            <p role="status" aria-live="polite" className="text-on-surface-variant">
              Loading {adapter.pluralLabel}…
            </p>
          ) : list.isError ? (
            <p role="alert" className="text-sm font-medium text-error">
              {loadErrorMessage}
            </p>
          ) : items.length === 0 ? (
            <p className="text-sm text-on-surface-variant">{adapter.emptyMessage}</p>
          ) : (
            <Table data-testid={adapter.tableTestId}>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const id = adapter.getId(item);
                  const deleted = adapter.isDeleted(item);
                  return (
                    <TableRow
                      key={id}
                      data-deleted={deleted ? "true" : undefined}
                      className={deleted ? "text-on-surface-muted" : undefined}
                    >
                      <TableCell>{adapter.getName(item)}</TableCell>
                      <TableCell>{formatDate(adapter.getCreatedAt(item))}</TableCell>
                      <TableCell>{deleted ? "Deleted" : "Active"}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          {deleted ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={restoreMutation.isPending}
                              onClick={() => {
                                restoreMutation.mutate({ id });
                              }}
                            >
                              Restore
                            </Button>
                          ) : (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setFormDialog({
                                    open: true,
                                    mode: "edit",
                                    item,
                                  });
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="text-danger hover:bg-danger-soft"
                                onClick={() => {
                                  setDeleteDialog({ open: true, item });
                                }}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NamedResourceFormDialog
        adapter={adapter}
        open={formDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setFormDialog({ open: false });
          }
        }}
        mode={formDialog.open ? formDialog.mode : "create"}
        initialItem={
          formDialog.open && formDialog.mode === "edit" ? formDialog.item : null
        }
      />
      <DeleteNamedResourceDialog
        adapter={adapter}
        open={deleteDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialog({ open: false, item: null });
          }
        }}
        item={deleteDialog.item}
      />
    </section>
  );
}
